import React, { useState, useMemo, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../auth/AuthContext';
import { useToast } from './Toast';
import { extractErrorMessage } from '../utils/errors';

const API_URL = process.env.REACT_APP_API_URL || '/api';

interface TagRequirement {
  key: string;
  allowedValues: string[];
}

interface TagPolicyDto {
  requiredTags: TagRequirement[];
  updatedAtUtc: string;
  updatedBy: string;
}

interface Resource {
  id: string;
  name: string;
  type: string;
  resourceGroup: string;
  subscriptionId: string;
  tags: Record<string, string>;
}

interface Props {
  resources: Resource[];
  onFixResources: (resourceIds: string[], tags: Record<string, string>) => void;
}

const ComplianceDashboard: React.FC<Props> = ({ resources, onFixResources }) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const isAdmin = user?.role === 'admin';

  // The required-tags policy is a shared, server-side document (admin-managed) —
  // previously every browser kept its own copy in localStorage.
  const [policy, setPolicy] = useState<TagPolicyDto | null>(null);
  const [newTag, setNewTag] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'compliant' | 'non-compliant'>('all');
  const [searchText, setSearchText] = useState('');

  const loadPolicy = useCallback(async () => {
    try {
      const res = await axios.get<TagPolicyDto>(`${API_URL}/tagpolicy`);
      setPolicy(res.data);
    } catch (e) {
      showToast('Failed to load tag policy.', 'error');
    }
  }, [showToast]);

  useEffect(() => { loadPolicy(); }, [loadPolicy]);

  const requirements = useMemo(() => policy?.requiredTags ?? [], [policy]);

  const savePolicy = async (requiredTags: TagRequirement[]) => {
    try {
      const res = await axios.put<TagPolicyDto>(`${API_URL}/tagpolicy`, { requiredTags });
      setPolicy(res.data);
      showToast('Tag policy updated.', 'success');
    } catch (e) {
      showToast(extractErrorMessage(e, 'Failed to update tag policy.'), 'error');
    }
  };

  const addRequiredTag = () => {
    const key = newTag.trim();
    if (key && !requirements.some(r => r.key.toLowerCase() === key.toLowerCase())) {
      savePolicy([...requirements, { key, allowedValues: [] }]);
      setNewTag('');
    }
  };

  const removeRequiredTag = (key: string) =>
    savePolicy(requirements.filter(r => r.key !== key));

  const complianceData = useMemo(() => {
    return resources.map(r => {
      // Missing when the tag is absent — or present with a value outside the allow-list.
      const missingTags = requirements
        .filter(req => {
          const value = r.tags[req.key];
          if (!value) return true;
          return req.allowedValues.length > 0 && !req.allowedValues.includes(value);
        })
        .map(req => req.key);
      return {
        ...r,
        missingTags,
        isCompliant: missingTags.length === 0,
        complianceScore: requirements.length === 0 ? 100 : Math.round(((requirements.length - missingTags.length) / requirements.length) * 100)
      };
    });
  }, [resources, requirements]);

  const stats = useMemo(() => {
    const total = complianceData.length;
    const compliant = complianceData.filter(r => r.isCompliant).length;
    const pct = total === 0 ? 0 : Math.round((compliant / total) * 100);

    const byGroup: Record<string, { total: number; compliant: number }> = {};
    complianceData.forEach(r => {
      if (!byGroup[r.resourceGroup]) byGroup[r.resourceGroup] = { total: 0, compliant: 0 };
      byGroup[r.resourceGroup].total++;
      if (r.isCompliant) byGroup[r.resourceGroup].compliant++;
    });

    return { total, compliant, nonCompliant: total - compliant, pct, byGroup };
  }, [complianceData]);

  const filtered = useMemo(() => {
    return complianceData
      .filter(r => filterStatus === 'all' || (filterStatus === 'compliant' ? r.isCompliant : !r.isCompliant))
      .filter(r => !searchText || r.name.toLowerCase().includes(searchText.toLowerCase()) || r.resourceGroup.toLowerCase().includes(searchText.toLowerCase()));
  }, [complianceData, filterStatus, searchText]);

  const fixAll = () => {
    const nonCompliant = complianceData.filter(r => !r.isCompliant);
    if (nonCompliant.length === 0) return;
    const defaultTags: Record<string, string> = {};
    requirements.forEach(req => {
      defaultTags[req.key] = req.allowedValues[0] ?? `[${req.key}]`;
    });
    onFixResources(nonCompliant.map(r => r.id), defaultTags);
  };

  const statusColor = stats.pct >= 80 ? '#28a745' : stats.pct >= 50 ? '#ffc107' : '#dc3545';

  return (
    <div style={{ padding: '0' }}>
      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Total Resources', value: stats.total, color: '#007bff' },
          { label: 'Compliant', value: stats.compliant, color: '#28a745' },
          { label: 'Non-Compliant', value: stats.nonCompliant, color: '#dc3545' },
          { label: 'Compliance Rate', value: `${stats.pct}%`, color: statusColor },
        ].map(card => (
          <div key={card.label} style={{ background: '#25252a', border: '1px solid #444', borderRadius: '8px', padding: '1.25rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: card.color }}>{card.value}</div>
            <div style={{ color: '#b0b0b0', fontSize: '0.85rem', marginTop: '0.25rem' }}>{card.label}</div>
          </div>
        ))}
      </div>

      {/* Compliance bar */}
      <div style={{ background: '#25252a', border: '1px solid #444', borderRadius: '8px', padding: '1.25rem', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <span style={{ color: '#f0f0f0', fontWeight: 500 }}>Overall Compliance</span>
          <span style={{ color: statusColor, fontWeight: 700 }}>{stats.pct}%</span>
        </div>
        <div style={{ height: '12px', background: '#333', borderRadius: '6px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${stats.pct}%`, background: statusColor, borderRadius: '6px', transition: 'width 0.5s ease' }} />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '1rem' }}>
          {Object.entries(stats.byGroup).map(([group, data]) => {
            const groupPct = Math.round((data.compliant / data.total) * 100);
            const gc = groupPct >= 80 ? '#28a745' : groupPct >= 50 ? '#ffc107' : '#dc3545';
            return (
              <div key={group} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: '#1a1a1e', borderRadius: '6px', padding: '0.35rem 0.65rem' }}>
                <span style={{ color: '#b0b0b0', fontSize: '0.8rem' }}>{group}</span>
                <span style={{ color: gc, fontWeight: 600, fontSize: '0.85rem' }}>{groupPct}%</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Required tags policy (shared, admin-managed) */}
      <div style={{ background: '#25252a', border: '1px solid #444', borderRadius: '8px', padding: '1.25rem', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h3 style={{ margin: '0 0 0.75rem', color: '#f0f0f0', fontSize: '0.95rem' }}>Required Tags (organization policy)</h3>
          {policy && policy.updatedBy !== 'default' && (
            <span style={{ color: '#666', fontSize: '0.75rem' }}>
              Updated by {policy.updatedBy} · {new Date(policy.updatedAtUtc).toLocaleString()}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
          {requirements.map(req => (
            <span key={req.key} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: '#007bff22', color: '#4da6ff', border: '1px solid #007bff44', borderRadius: '12px', padding: '0.2rem 0.65rem', fontSize: '0.85rem' }}>
              {req.key}{req.allowedValues.length > 0 && <span style={{ color: '#88b8f0', fontSize: '0.75rem' }}>({req.allowedValues.join(' | ')})</span>}
              {isAdmin && (
                <button onClick={() => removeRequiredTag(req.key)} style={{ background: 'none', border: 'none', color: '#4da6ff', cursor: 'pointer', padding: '0', lineHeight: 1, fontSize: '0.9rem' }}>&#x2715;</button>
              )}
            </span>
          ))}
          {requirements.length === 0 && policy && (
            <span style={{ color: '#888', fontSize: '0.85rem' }}>No required tags defined — every resource is compliant.</span>
          )}
        </div>
        {isAdmin ? (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              value={newTag}
              onChange={e => setNewTag(e.target.value)}
              placeholder="Add required tag..."
              onKeyDown={e => e.key === 'Enter' && addRequiredTag()}
              style={{ background: '#1a1a1e', color: '#f0f0f0', border: '1px solid #555', borderRadius: '6px', padding: '0.45rem 0.75rem', fontSize: '0.875rem' }}
            />
            <button onClick={addRequiredTag} style={{ padding: '0.45rem 1rem', background: '#007bff', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Add</button>
          </div>
        ) : (
          <div style={{ color: '#888', fontSize: '0.8rem' }}>The policy is managed by administrators and applies to everyone.</div>
        )}
      </div>

      {/* Table controls */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          placeholder="Search resources..."
          style={{ background: '#25252a', color: '#f0f0f0', border: '1px solid #444', borderRadius: '6px', padding: '0.5rem 0.75rem', minWidth: '200px' }}
        />
        {(['all', 'compliant', 'non-compliant'] as const).map(s => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            style={{ padding: '0.5rem 1rem', background: filterStatus === s ? '#007bff' : '#25252a', color: filterStatus === s ? 'white' : '#b0b0b0', border: '1px solid ' + (filterStatus === s ? '#007bff' : '#444'), borderRadius: '6px', cursor: 'pointer', textTransform: 'capitalize' }}
          >
            {s}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <button
          onClick={fixAll}
          disabled={stats.nonCompliant === 0}
          style={{ padding: '0.5rem 1.25rem', background: stats.nonCompliant === 0 ? '#333' : '#dc354522', color: stats.nonCompliant === 0 ? '#666' : '#ff8f9a', border: '1px solid ' + (stats.nonCompliant === 0 ? '#444' : '#dc3545'), borderRadius: '6px', cursor: stats.nonCompliant === 0 ? 'not-allowed' : 'pointer' }}
        >
          Fix All Non-Compliant ({stats.nonCompliant})
        </button>
      </div>

      {/* Compliance table */}
      <div style={{ background: '#25252a', border: '1px solid #444', borderRadius: '8px', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ background: '#333' }}>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#b0b0b0', fontWeight: 500 }}>Resource</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#b0b0b0', fontWeight: 500 }}>Resource Group</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#b0b0b0', fontWeight: 500 }}>Missing Tags</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#b0b0b0', fontWeight: 500 }}>Score</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#b0b0b0', fontWeight: 500 }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 100).map((r, i) => (
              <tr key={r.id} style={{ borderTop: '1px solid #333', background: i % 2 === 0 ? 'transparent' : '#1a1a1e22' }}>
                <td style={{ padding: '0.65rem 1rem', color: '#f0f0f0' }}>{r.name}</td>
                <td style={{ padding: '0.65rem 1rem', color: '#b0b0b0' }}>{r.resourceGroup}</td>
                <td style={{ padding: '0.65rem 1rem' }}>
                  {r.missingTags.length === 0 ? (
                    <span style={{ color: '#28a745', fontSize: '0.8rem' }}>None</span>
                  ) : (
                    <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                      {r.missingTags.map(t => (
                        <span key={t} style={{ background: '#dc354522', color: '#ff8f9a', border: '1px solid #dc354544', borderRadius: '10px', padding: '0.1rem 0.5rem', fontSize: '0.75rem' }}>{t}</span>
                      ))}
                    </div>
                  )}
                </td>
                <td style={{ padding: '0.65rem 1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ flex: 1, height: '6px', background: '#333', borderRadius: '3px', overflow: 'hidden', minWidth: '60px' }}>
                      <div style={{ height: '100%', width: `${r.complianceScore}%`, background: r.isCompliant ? '#28a745' : r.complianceScore >= 50 ? '#ffc107' : '#dc3545', borderRadius: '3px' }} />
                    </div>
                    <span style={{ color: '#b0b0b0', fontSize: '0.78rem', width: '32px' }}>{r.complianceScore}%</span>
                  </div>
                </td>
                <td style={{ padding: '0.65rem 1rem' }}>
                  <span style={{
                    display: 'inline-block', padding: '0.15rem 0.6rem', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 600,
                    background: r.isCompliant ? '#28a74522' : '#dc354522',
                    color: r.isCompliant ? '#90ee90' : '#ff8f9a',
                    border: `1px solid ${r.isCompliant ? '#28a74544' : '#dc354544'}`
                  }}>
                    {r.isCompliant ? 'Compliant' : 'Non-Compliant'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length > 100 && (
          <div style={{ padding: '0.75rem 1rem', color: '#888', fontSize: '0.85rem', borderTop: '1px solid #333' }}>
            Showing 100 of {filtered.length} resources
          </div>
        )}
        {filtered.length === 0 && (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>No resources match the current filter</div>
        )}
      </div>
    </div>
  );
};

export default ComplianceDashboard;
